import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { fetchCurrentUser } from "./api";
import { logout as httpLogout, onAuthFailure, refreshAccessToken } from "./http";
import {
  clearSession,
  getAccessToken,
  getUser,
  setSession,
  setUser,
  subscribe,
  type AuthUser,
} from "./store";

type Status = "loading" | "authed" | "unauthed";

interface AuthContextValue {
  status: Status;
  user: AuthUser | null;
  // Replaces the in-memory access token + user. Components in the login flow
  // call this after a successful /auth/login (single-factor) or
  // /auth/2fa/verify response.
  setAuthed: (input: { accessToken: string; user: AuthUser }) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setLocalUser] = useState<AuthUser | null>(() => getUser());
  const bootstrappedRef = useRef(false);

  // Mirror the module-level store into React state so consumers re-render.
  useEffect(() => {
    return subscribe(() => {
      const next = getUser();
      setLocalUser(next);
      // We deliberately do not flip `status` here — bootstrap and explicit
      // login/logout transitions are the only things that should change it.
      if (next === null && getAccessToken() === null) {
        // External clearSession() (e.g. logout from another tab via 401) —
        // surface as unauthenticated.
        setStatus("unauthed");
      }
    });
  }, []);

  // Bootstrap: try a silent refresh, then /auth/me to recover the session.
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    let cancelled = false;
    (async () => {
      const token = await refreshAccessToken();
      if (cancelled) return;
      if (!token) {
        clearSession();
        setStatus("unauthed");
        return;
      }
      try {
        const me = await fetchCurrentUser();
        if (cancelled) return;
        if (!me) {
          clearSession();
          setStatus("unauthed");
          return;
        }
        setUser(me);
        setStatus("authed");
      } catch {
        if (cancelled) return;
        clearSession();
        setStatus("unauthed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The HTTP layer surfaces auth failures (refresh dead, manual logout) here
  // so we can transition the React tree to "unauthed" and let guards bounce
  // to /login.
  useEffect(() => {
    return onAuthFailure(() => {
      setStatus("unauthed");
    });
  }, []);

  const setAuthed = useCallback(
    (input: { accessToken: string; user: AuthUser }) => {
      setSession(input);
      setStatus("authed");
    },
    [],
  );

  const logout = useCallback(async () => {
    await httpLogout();
    setStatus("unauthed");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, setAuthed, logout }),
    [status, user, setAuthed, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
